/**
 * Column definitions for ExecutionsByStatusPage. One row per matching
 * ExecutionSummary.
 *
 * Status / State / Data source columns are intentionally omitted — they
 * echo the user's search criteria (every row on a given page has the
 * same value) and don't add information. Consumers who need those
 * fields for a specific execution can drill into the payment detail.
 */
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import { useNavigate } from 'react-router-dom';
import { CopyableText } from 'src/components/common/CopyableText';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';
import { ExecutionSummary } from 'src/store/slices/executionsByStatus/executionsByStatusSlice';

/**
 * ExecutionId cell — clickable via SPA routing when the execution has
 * at least one paymentId (payment detail page is the closest existing
 * surface that renders an execution's context). Uses `onFollow` with
 * `navigate` rather than a plain `href` so the click stays within the
 * SPA and preserves Redux state.
 */
const ExecutionIdCell = ({ row }: { row: ExecutionSummary }) => {
  const navigate = useNavigate();
  const firstPaymentId = row.paymentIds?.[0];
  if (!firstPaymentId) return <>{row.id}</>;
  const href = `/payment?id=${encodeURIComponent(firstPaymentId)}`;
  return (
    <Link href={href} onFollow={(e) => { e.preventDefault(); navigate(href); }}>
      {row.id}
    </Link>
  );
};

/**
 * File name cell — comma-joined executionTags rendered via CopyableText
 * so operators can copy the exact file identifier for cross-referencing
 * in other tools. Renders plain '—' when the row carries no tags (rare
 * — some inbound bank-response executions before matching).
 */
const FileNameCell = ({ row }: { row: ExecutionSummary }) => {
  const tags = row.executionTags ?? [];
  if (tags.length === 0) return <>—</>;
  return <CopyableText value={tags.join(', ')} />;
};

export const EXECUTIONS_BY_STATUS_COLUMN_DEFINITIONS = [
  {
    id: 'id',
    header: 'Execution ID',
    cell: (row: ExecutionSummary) => <ExecutionIdCell row={row} />,
  },
  {
    id: 'executionType',
    header: 'Type',
    cell: (row: ExecutionSummary) => <Badge>{row.executionType}</Badge>,
  },
  {
    id: 'fileName',
    header: 'File name',
    cell: (row: ExecutionSummary) => <FileNameCell row={row} />,
  },
  {
    id: 'pluginName',
    header: 'Plugin',
    cell: (row: ExecutionSummary) => row.pluginName || '—',
  },
  {
    id: 'paymentIds',
    header: 'Payments',
    cell: (row: ExecutionSummary) => row.paymentIds?.length ?? 0,
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    cell: (row: ExecutionSummary) => formatIsoTimestamp(row.updatedAt),
    /** Enables the sort icon in the header. Only sortable field the API supports. */
    sortingField: 'updatedAt',
  },
  {
    id: 'totalLatencyMs',
    header: 'Total latency',
    cell: (row: ExecutionSummary) => formatLatency(row.totalLatencyMs),
  },
];
